import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useWidget } from '@telescope/cassini-hooks';
import { Prize } from '../types';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils';
import { SpinTheWheelOpen } from 'types';
import { ErrorType } from 'types';
import { useSpinToWin } from 'features';

export interface SpinnerContextValue {
  prizes: Prize[]
  target: number
}

const SpinnerContext = createContext<SpinnerContextValue | undefined>(undefined);

export const SpinnerProvider = ({ children }: { children: React.ReactElement }) => {
  const { data: prizesData } = useWidget({ select: (data: SpinTheWheelOpen) => data.snapshot.snapshot_views.prizes });
  const { winningPrizes, losingPrize } = prizesData!;
  const {
    data: wheelData,
  } = useWidget({ select: (data: SpinTheWheelOpen) => data.snapshot.snapshot_views.wheel });
  const { settings: { losingPrizesCount = 0 }} = wheelData!;

  const prizes = useMemo(() => {
    if (!losingPrizesCount) return winningPrizes;
    const slotsAmount = winningPrizes.length + losingPrizesCount;

    const initialPrizes = [...winningPrizes];
    return Array.from({ length: slotsAmount }).reduce((acc: any[], _: any, i: number) => {
      acc[i] = (i % 2 !== 0 && i < 2 * losingPrizesCount)? losingPrize : (initialPrizes.shift() || losingPrize);
      return acc;
    }, []);
  }, [winningPrizes, losingPrizesCount, losingPrize])

  const { prizeId, isAnimating } = useSpinToWin();
  
  const navigate = useNavigate();

  const getTarget = useCallback((): number => {
    const index = prizes.findIndex((p) => {
      return prizeId? prizeId == p.id : !p.id;
    })

    // If no prize is found in the UI, handle error
    if (index === -1) {
      navigate(`${ROUTES.ERROR}/${ErrorType.GENERIC}`);
    }

    return index;
  }, [prizes, prizeId]);

  const [target, setTarget] = useState<number>(getTarget());

  useEffect(() => {
    if (!isAnimating) return;

    const value = getTarget()
    setTarget(value);
  }, [isAnimating, prizeId])

  const value = useMemo(() => ({
    prizes,
    target
  }), [
    prizes,
    target
  ]);

  return <SpinnerContext.Provider value={value}>{children}</SpinnerContext.Provider>;
};

export function useSpinner() {
  const context = useContext(SpinnerContext);

  if (context === undefined) {
    throw new Error('useSpinner must be used within a SpinnerProvider');
  }

  return context;
}